import React, { useState, useCallback } from 'react';
import { Polygon, Polyline, Circle, Marker } from '@react-google-maps/api';
import useLongPress from '../CustomHooks/useLongPress';
import { getVertexRadius } from '@jumbo/utils/mapHelpers';

const RenderPolygons = ({
  polygons,
  selected,
  setSelected,
  currentPolygon,
  mode,
  setPolygons,
  setCurrentPolygonData,
  setCurrentPolygon,
  setShowPolygonDialog,
  onUpdatePolygon,
  zoom,
}) => {
  const [draggingVertex, setDraggingVertex] = useState(null);

  const { start, cancel, wasLongPress, shouldBlockDrag } = useLongPress(
    useCallback(({ polyId, vertexIdx }) => {
      setDraggingVertex({ polyId, vertexIdx });
    }, []),
  );

  // In-progress polygon — local state only, no API
  const handleCurrentVertexDragEnd = useCallback(
    (e, vertexIdx) => {
      setCurrentPolygon(prev => {
        if (!prev) return prev;
        const newCoords = prev.coords.map((c, i) => (i === vertexIdx ? { lat: e.latLng.lat(), lng: e.latLng.lng() } : c));
        return { ...prev, coords: newCoords };
      });
      setDraggingVertex(null);
    },
    [setCurrentPolygon],
  );

  // Saved polygon — local update + API call
  const handleSavedVertexDragEnd = useCallback(
    async (e, poly, vertexIdx) => {
      const newCoords = poly.coords.map((c, i) => (i === vertexIdx ? { lat: e.latLng.lat(), lng: e.latLng.lng() } : c));
      const updatedPoly = { ...poly, coords: newCoords };

      setPolygons(prev => prev.map(p => (p.id === poly.id ? updatedPoly : p)));
      setDraggingVertex(null);

      try {
        await onUpdatePolygon?.(updatedPoly); // ← only called for existing polygons
      } catch (err) {
        console.error('Failed to update polygon vertex:', err);
        setPolygons(prev => prev.map(p => (p.id === poly.id ? poly : p))); // rollback
      }
    },
    [setPolygons, onUpdatePolygon],
  );

  return (
    <>
      {/* ── Saved polygons ── */}
      {polygons
        .filter(poly => poly.coords && poly.coords.length >= 3)
        .map((poly, index) => {
          const { id, coords, mapLabel, label, hexColor } = poly;
          const isSelected = selected?.id === id;

          return (
            <React.Fragment key={`polygon-${label ?? mapLabel}-${index}`}>
              <Polygon
                paths={coords}
                options={{
                  clickable: mode === null,
                  fillColor: hexColor || '#800080',
                  fillOpacity: 0.3,
                  strokeColor: hexColor || '#800080',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }}
                onClick={() => {
                  mode === null && setSelected({ ...poly, mapType: 'polygon' });
                }}
              />

              {/* Vertex handles — only when selected */}
              {isSelected &&
                mode === null &&
                coords.map((coord, vIdx) => {
                  const isDragging = draggingVertex?.polyId === id && draggingVertex?.vertexIdx === vIdx;

                  return (
                    <Marker
                      key={`saved-vertex-${id}-${vIdx}`}
                      position={coord}
                      draggable={true} // ← always true
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: isDragging ? 9 : 6,
                        fillColor: isDragging ? '#FFD700' : '#FFFFFF',
                        fillOpacity: 1,
                        strokeColor: hexColor || '#800080',
                        strokeWeight: 2,
                      }}
                      onMouseDown={() => start({ polyId: id, vertexIdx: vIdx })}
                      onMouseUp={cancel}
                      onDragStart={e => {
                        if (shouldBlockDrag()) {
                          // Cancel the drag by moving marker back to original position
                          e.stop();
                        }
                      }}
                      onDragEnd={e => {
                        if (!wasLongPress()) return; // ignore drags that slipped through
                        handleSavedVertexDragEnd(e, poly, vIdx);
                      }}
                      onClick={() => {
                        if (wasLongPress()) return;
                      }}
                      options={{ zIndex: isDragging ? 1000 : 100 }}
                    />
                  );
                })}
            </React.Fragment>
          );
        })}

      {/* ── In-progress polygon preview ── */}
      {currentPolygon?.coords?.length > 1 && (
        <Polyline
          path={currentPolygon.coords}
          options={{
            strokeColor: 'rgba(0,0,255,0.5)',
            strokeOpacity: 0.8,
            strokeWeight: 4,
          }}
        />
      )}

      {/* ── In-progress polygon vertices ── */}
      {currentPolygon?.coords?.map((coord, idx) => {
        const isDragging = draggingVertex?.polyId === 'current' && draggingVertex?.vertexIdx === idx;

        return (
          <Circle
            key={`current-vertex-${idx}`}
            draggable={true}
            center={coord}
            radius={getVertexRadius(zoom)}
            options={{
              strokeColor: '#FFFFFF',
              strokeOpacity: 1,
              strokeWeight: 2,
              fillColor: isDragging ? '#FFD700' : 'rgba(0,0,255,0.5)',
              fillOpacity: 0.7,
              clickable: true,
              zIndex: isDragging ? 999 : 1,
            }}
            onMouseDown={() => start({ polyId: 'current', vertexIdx: idx })}
            onMouseUp={cancel}
            onDragStart={e => {
              if (shouldBlockDrag()) e.stop();
            }}
            onDragEnd={e => {
              if (!wasLongPress()) return;
              handleCurrentVertexDragEnd(e, idx);
            }}
            onClick={e => {
              if (wasLongPress()) return;

              console.log('VERTEX CLICK', idx);
              if (idx === 0 && currentPolygon.coords.length >= 3) {
                e.stop();
                setPolygons(prev => [...prev, currentPolygon]);
                setCurrentPolygonData(currentPolygon);
                setCurrentPolygon(null);
                setShowPolygonDialog(true);
              } else {
                setCurrentPolygon(prev => ({
                  ...prev,
                  coords: prev.coords.filter((_, i) => i !== idx),
                }));
              }
            }}
          />
        );
      })}
    </>
  );
};

export default RenderPolygons;
